import React from 'react';
import { motion } from 'motion/react';
import { MessageSquare, MapPin, Home, Bed, Bath, Maximize, Clock, Building, ExternalLink, Eye } from 'lucide-react';
import { StockPost } from './StockSharingFeed';

interface StockCardProps {
  post: StockPost;
  onOpenChat: (post: StockPost) => void;
  onViewProperty?: (propertyId: string) => void; // For navigating to internal properties
}

export function StockCard({ post, onOpenChat, onViewProperty }: StockCardProps) {
  const getTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return `${Math.floor(seconds / 604800)} weeks ago`;
  };

  const isInactive = post.status !== 'ACTIVE';
  const messageCount = post.messages.length;

  return (
    <motion.div
      data-testid="stock-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative p-6 rounded-2xl border transition-all ${
        isInactive
          ? 'bg-white/5 border-white/10 opacity-60'
          : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#FDB022]/30'
      }`}
    >
      {/* Status Badge */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        {isInactive && (
          <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 border border-white/20">
            {post.status}
          </span>
        )}
        <span
          className={`px-3 py-1 text-xs font-bold rounded-full ${
            post.type === 'BUY'
              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              : 'bg-green-500/20 text-green-400 border border-green-500/30'
          }`}
        >
          {post.type}
        </span>
      </div>

      {/* Property Title */}
      <h3 data-testid="stock-card-title" className="text-xl font-bold mb-3 pr-32">{post.propertyTitle}</h3>

      {/* Property Details */}
      <div className="space-y-3 mb-4">
        <div className="flex flex-wrap gap-4 text-sm">
          <div data-testid="stock-card-location" className="flex items-center gap-2 text-white/80">
            <MapPin className="w-4 h-4 text-[#FDB022]" />
            {post.location}
          </div>
          <div data-testid="stock-card-type" className="flex items-center gap-2 text-white/80">
            <Home className="w-4 h-4 text-[#FDB022]" />
            {post.propertyType}
          </div>
          {post.bedrooms && (
            <div data-testid="stock-card-bedrooms" className="flex items-center gap-2 text-white/80">
              <Bed className="w-4 h-4 text-[#FDB022]" />
              {post.bedrooms} Bed
            </div>
          )}
          {post.bathrooms && (
            <div data-testid="stock-card-bathrooms" className="flex items-center gap-2 text-white/80">
              <Bath className="w-4 h-4 text-[#FDB022]" />
              {post.bathrooms} Bath
            </div>
          )}
          {post.size && (
            <div className="flex items-center gap-2 text-white/80">
              <Maximize className="w-4 h-4 text-[#FDB022]" />
              {post.size} m²
            </div>
          )}
        </div>

        {/* Price */}
        <div data-testid="stock-card-price" className="text-2xl font-bold text-[#FDB022]">{post.price}</div>

        {/* Description */}
        <p className="text-white/70 line-clamp-2">{post.description}</p>

        {/* Listing Links */}
        {(post.internalPropertyId || post.externalListingUrl) && (
          <div className="flex flex-wrap gap-2 mt-3">
            {post.internalPropertyId && onViewProperty && (
              <motion.button
                onClick={() => onViewProperty(post.internalPropertyId!)}
                className="flex items-center gap-2 px-3 py-1.5 bg-[#FDB022]/20 hover:bg-[#FDB022]/30 text-[#FDB022] text-sm font-medium rounded-lg border border-[#FDB022]/30 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Eye className="w-4 h-4" />
                View on Our Catalog
              </motion.button>
            )}
            {post.externalListingUrl && (
              <motion.a
                href={post.externalListingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-sm font-medium rounded-lg border border-blue-500/30 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ExternalLink className="w-4 h-4" />
                View External Listing
              </motion.a>
            )}
          </div>
        )}
      </div>

      {/* Agent Info & Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#FDB022]/20 flex items-center justify-center">
            <span className="text-[#FDB022] font-bold">
              {(post.agentName || 'U').split(' ').map(n => n[0]).join('')}
            </span>
          </div>
          <div>
            <p data-testid="stock-card-agent-name" className="font-medium text-sm">{post.agentName}</p>
            <div className="flex items-center gap-2 text-xs text-white/60">
              <Building className="w-3 h-3" />
              {post.agency}
              <span className="mx-1">•</span>
              <Clock className="w-3 h-3" />
              {getTimeAgo(post.timestamp)}
            </div>
          </div>
        </div>

        <motion.button
          data-testid="external-agent-chat-button"
          onClick={() => {
            console.log('Chat button clicked for post:', post.id);
            onOpenChat(post);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={isInactive}
        >
          <MessageSquare className="w-4 h-4" />
          {messageCount > 0 ? `View Chat (${messageCount})` : 'View & Chat'}
        </motion.button>
      </div>
    </motion.div>
  );
}